import  { useEffect, useState, } from 'react'
import { createUseStyles } from 'react-jss'
import { useNavigate, useParams } from 'react-router-dom';
import Card from '../components/Card'
import CardSection from '../components/CardSection'
import DetailsCard from '../components/DetailsCard'
import { Colors } from '../constants/colors'
import Button from '../components/Button'
import AcceptAmlFlaggedEntityModal from '../components/Aml/AcceptAmlFlaggedEntityModal'
import { formatDateString, formatDateTimeString } from '../utils/dateHelper'
import { useAppSelector } from '../redux/hooks';
import useCompanyRepresentativeAmlActions from '../hooks/useCompanyRepresentativeAmlActions';
import FullScreenLoadingIndicator from '../components/FullScreenLoadingIndicator';
import CountryFlag from '../components/CountryFlag';
import { titleMarginBottom } from '../constants/layout';
import NavLink from '../components/NavLink';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';

const CompanyRepresentativeAmlDetails = () => {
  const styles = useStyles()
  const { id } = useParams<{id: string}>();
  const navigate = useNavigate();

  const [acceptModalVisible, setAcceptModalVisible] = useState(false)
  const { getAmlFlaggedCompanyRepresentativeDetails, acceptAmlFlaggedCompanyRepresentative } = useCompanyRepresentativeAmlActions()

  const loading = useAppSelector(state => state.amlFlaggedCompanyRepresentative.loading)
  const representativeDetails = useAppSelector(state => state.amlFlaggedCompanyRepresentative.representativeDetails)

  useEffect(() => {
    if (representativeDetails === null || representativeDetails.id !== id) {
      if (id) getAmlFlaggedCompanyRepresentativeDetails(id)
    }
  }, [])

  const handleOnAccept = (comment: string) => {
    if (id) acceptAmlFlaggedCompanyRepresentative(id, comment)
    setAcceptModalVisible(false)
  }

  const renderTransitionHistoryHeader = () => {
    return (
      <Card className={styles.listContainer}>
        <div className={styles.listCell}>
          <p>Date</p>
        </div>
        <div className={styles.listCell}>
          <p>From</p>
        </div>
        <div className={styles.listCell}>
          <p>To</p>
        </div>
      </Card>
    )
  }

  const renderTransitionHistoryList = () => {
    return representativeDetails?.amlDetails.stateTransitions?.map((transition, index) => {
      return (
        <Card className={styles.listContainer} key={`transition${index}`}>
          <div className={styles.listCell}>
            <p><span>{formatDateTimeString(transition.date)}</span></p>
          </div>
          <div className={styles.listCell}>
            <p><span>{transition.from}</span></p>
          </div>
          <div className={styles.listCell}>
            <p><span>{transition.to}</span></p>
          </div>
        </Card>
      )
    })
  }

  const renderManualAcceptHistoryHeader = () => {
    return (
      <Card className={styles.listContainer}>
        <div className={styles.listCell}>
          <p>Date</p>
        </div>
        <div className={styles.listCell}>
          <p>Comment</p>
        </div>
      </Card>
    )
  }

  const renderManualAcceptHistoryList = () => {
    return representativeDetails?.amlDetails.manualAccepts?.map((accept, index) => {
      return (
        <Card className={styles.listContainer} key={`accept${index}`}>
          <div className={styles.listCell}>
            <p><span>{formatDateTimeString(accept.date)}</span></p>
          </div>
          <div className={styles.listCell}>
            <p><span>{accept.comment}</span></p>
          </div>
        </Card>
      )
    })
  }

  const renderReports = () => {
    return representativeDetails?.amlDetails.reports?.map((report, index) => {
      return (
        <Card className={styles.card} key={`report${index}`}>
          <pre id="json">{ report as any }</pre>
        </Card>
      )
    })
  }

  const notSpecified = 'not specified'

  return (
    <div className={styles.container}>
      <h1>PEP &amp; sanction details: {representativeDetails?.givenName} {representativeDetails?.familyName}</h1>
      <div className={styles.actionBar}>
        <div className={styles.actionBarCell} style={{justifyContent: 'flex-start'}}>
          <NavLink title='Back' icon={faArrowLeft} onClick={() => navigate(-1)} />
        </div>
        <div className={styles.actionBarCell} style={{justifyContent: 'flex-end'}}>
            <div style={{paddingBottom: 8}}>
              { representativeDetails?.amlDetails.state === 'flagged' &&  <Button className={styles.actionButton} title='Accept' onClick={() => setAcceptModalVisible(true)} />  }
            </div>
        </div>
      </div>
      <CardSection>
        <DetailsCard>
          <h6>General information</h6>
          <p>Name: <span>{representativeDetails?.givenName} {representativeDetails?.familyName}</span></p>
          <p>Country: <span>
            {representativeDetails?.country
              ? <><CountryFlag country={representativeDetails.country}/>{' '}<span style={{textTransform: 'uppercase'}}>{representativeDetails.country}</span></>
              : notSpecified}
          </span></p>
          <p>Date of birth: <span>{formatDateString(representativeDetails?.dateOfBirth ?? '')}</span></p>
          <p>Company: <span>{representativeDetails?.companyName} ({representativeDetails?.companyRegistrationNumber})</span></p>
          <p>State: <span>{representativeDetails?.amlDetails.state}</span></p>
        </DetailsCard>
      </CardSection>
      <CardSection>
        <DetailsCard>
          <h6>State transitions</h6>
          {renderTransitionHistoryHeader()}
          {renderTransitionHistoryList()}
        </DetailsCard>
        <DetailsCard>
          <h6>Manual accept history</h6>
          {renderManualAcceptHistoryHeader()}
          {renderManualAcceptHistoryList()}
        </DetailsCard>
      </CardSection>
      <CardSection>
        <DetailsCard>
          <h6>Reports</h6>
          {renderReports()}
        </DetailsCard>
      </CardSection>
      <AcceptAmlFlaggedEntityModal
        visible={acceptModalVisible}
        onSaveClick={handleOnAccept}
        onCancelClick={() => setAcceptModalVisible(false)}
      />
      <FullScreenLoadingIndicator visible={loading} />
    </div>
  )
}

export default CompanyRepresentativeAmlDetails

const useStyles = createUseStyles({
  container: {
    '& h1': {
      marginBottom: titleMarginBottom
    },
    '& h2': {
      paddingTop: '30px'
    }
  },
  listContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    textAlign: 'left',
    transition: '0.4s'
  },
  listCell: {
    flex: 1
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'left',
    textAlign: 'left',
    padding: '5px 5px',
    margin: '10px 0',
    backgroundColor: Colors.darkGrayCard,
  },
  actionBar: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  actionBarCell: {
    display: 'flex',
    flexDirection: 'row',
    columnGap: 15,
    alignItems: 'center',
    width: '33%'
  },
  actionButton: {
    margin: '5px'
  }
})
